/**
 * useDataGridUndo
 *
 * Manages an undo/redo stack for cell edits. Each undo dispatches a
 * reverse `set_value` mutation to keep the server in sync.
 *
 * Stack is capped at 50 entries and cleared on edit mode exit.
 */

import { useRef, useState, useCallback } from 'react';
import type { CellEdit, UndoEntry } from './types';

const MAX_UNDO_ENTRIES = 50;

export interface UseDataGridUndoOptions {
  saveCell: (edit: CellEdit) => Promise<boolean>;
  /** Ref that is true during a paste operation — blocks undo/redo */
  isPastingRef: React.RefObject<boolean>;
}

export interface UseDataGridUndoReturn {
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  canUndo: boolean;
  canRedo: boolean;
  pushEntry: (entry: UndoEntry) => void;
  clearStack: () => void;
}

export function useDataGridUndo(opts: UseDataGridUndoOptions): UseDataGridUndoReturn {
  const undoStackRef = useRef<UndoEntry[]>([]);
  const redoStackRef = useRef<UndoEntry[]>([]);
  const [, setTick] = useState(0);
  const forceUpdate = useCallback(() => setTick((t) => t + 1), []);

  const pushEntry = useCallback(
    (entry: UndoEntry) => {
      undoStackRef.current.push(entry);
      if (undoStackRef.current.length > MAX_UNDO_ENTRIES) {
        undoStackRef.current.shift();
      }
      // New edit clears redo stack
      redoStackRef.current = [];
      forceUpdate();
    },
    [forceUpdate],
  );

  /**
   * Pop all entries from the top of a stack that share the same batchId.
   * If the top entry has no batchId, just pop that single entry.
   */
  const popBatch = (stack: UndoEntry[]): UndoEntry[] => {
    const top = stack.pop();
    if (!top) return [];
    if (!top.batchId) return [top];
    const batch = [top];
    while (stack.length > 0) {
      const next = stack[stack.length - 1];
      if (next.batchId !== top.batchId) break;
      batch.push(stack.pop()!);
    }
    return batch;
  };

  const undo = useCallback(async () => {
    // Block during paste to prevent race conditions
    if (opts.isPastingRef.current) return;

    const batch = popBatch(undoStackRef.current);
    if (batch.length === 0) return;

    let allSuccess = true;
    for (const entry of batch) {
      const success = await opts.saveCell({
        doctype: entry.doctype,
        name: entry.name,
        fieldname: entry.fieldname,
        value: entry.oldValue,
        oldValue: entry.newValue,
      });
      if (!success) {
        allSuccess = false;
        break;
      }
    }

    if (!allSuccess) {
      // Undo failed — push all back so user can retry
      undoStackRef.current.push(...batch.reverse());
      forceUpdate();
      return;
    }

    redoStackRef.current.push(...batch);
    forceUpdate();
  }, [opts.saveCell, opts.isPastingRef, forceUpdate]);

  const redo = useCallback(async () => {
    if (opts.isPastingRef.current) return;

    const batch = popBatch(redoStackRef.current);
    if (batch.length === 0) return;

    let allSuccess = true;
    for (const entry of batch) {
      const success = await opts.saveCell({
        doctype: entry.doctype,
        name: entry.name,
        fieldname: entry.fieldname,
        value: entry.newValue,
        oldValue: entry.oldValue,
      });
      if (!success) {
        allSuccess = false;
        break;
      }
    }

    if (!allSuccess) {
      redoStackRef.current.push(...batch.reverse());
      forceUpdate();
      return;
    }

    undoStackRef.current.push(...batch);
    forceUpdate();
  }, [opts.saveCell, opts.isPastingRef, forceUpdate]);

  const clearStack = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    forceUpdate();
  }, [forceUpdate]);

  return {
    undo,
    redo,
    canUndo: undoStackRef.current.length > 0,
    canRedo: redoStackRef.current.length > 0,
    pushEntry,
    clearStack,
  };
}
